import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static targets = ["messages", "input", "onlineNumber", "emojiBtn"]

  // Allow-list of cosmetic rarity tiers, injected by the server from the Tier
  // registry (#435) via `data-live-chat-frame-tiers-value` so the list has one
  // source of truth and an operator-added tier reaches the client without a JS
  // rebuild. The `data-tier` attribute on the avatar wrapper is a CSS hook only
  // (#402 rarity glow); validating it here keeps a hostile payload from
  // smuggling arbitrary attribute content even though the frame markup itself
  // is server-rendered and pre-escaped. Falls back to the frozen baseline when
  // the value is absent/empty (pre-render or a stale fragment) so validation
  // never silently drops every tier.
  static values = { frameTiers: Array }

  static FALLBACK_FRAME_TIERS = ["common", "rare", "epic", "legendary", "seasonal"]

  get frameTiers() {
    const injected = this.frameTiersValue
    return injected && injected.length ? injected : this.constructor.FALLBACK_FRAME_TIERS
  }

  connect() {
    this.messageCount = 0
    this.maxMessages = 80
    this.currentUserId = document.querySelector("meta[name='current-user-id']")?.content

    this._subscribeToChannel()
  }

  disconnect() {
    if (this.channel) this.channel.unsubscribe()
  }

  toggleEmoji() {
    if (this._emojiPicker) {
      this._emojiPicker.remove()
      this._emojiPicker = null
      return
    }

    const categories = {
      "😊": ["😀","😃","😄","😁","😆","😅","🤣","😂","🙂","😊","😇","🥰","😍","🤩","😘","😗","😚","😙","🥲","😋","😛","😜","🤪","😝","🤑","🤗","🤭","🫢","🤫","🤔","🫡","🤐","🤨","😐","😑","😶","🫥","😏","😒","🙄","😬","🤥","😌","😔","😪","🤤","😴","😷","🤒","🤕","🤢","🤮","🥵","🥶","🥴","😵","🤯","🤠","🥳","🥸","😎","🤓","🧐","😕","🫤","😟","🙁","😮","😯","😲","😳","🥺","🥹","😦","😧","😨","😰","😥","😢","😭","😱","😖","😣","😞","😓","😩","😫","🥱","😤","😡","😠","🤬","😈","👿","💀","☠️","💩","🤡","👹","👺","👻","👽","👾","🤖"],
      "👋": ["👋","🤚","🖐️","✋","🖖","🫱","🫲","🫳","🫴","👌","🤌","🤏","✌️","🤞","🫰","🤟","🤘","🤙","👈","👉","👆","🖕","👇","☝️","🫵","👍","👎","✊","👊","🤛","🤜","👏","🙌","🫶","👐","🤲","🤝","🙏","💪","🦾","🦿","🦵","🦶","👂","🦻","👃","🧠","🫀","🫁","🦷","🦴","👀","👁️","👅","👄"],
      "❤️": ["❤️","🧡","💛","💚","💙","💜","🖤","🤍","🤎","💔","❤️‍🔥","❤️‍🩹","💕","💞","💓","💗","💖","💘","💝","💟","♥️","💋","💯","💢","💥","💫","💦","💨","🕳️","💤","💬","👁️‍🗨️","🗨️","🗯️","💭"],
      "🐶": ["🐶","🐱","🐭","🐹","🐰","🦊","🐻","🐼","🐻‍❄️","🐨","🐯","🦁","🐮","🐷","🐸","🐵","🙈","🙉","🙊","🐒","🐔","🐧","🐦","🐤","🐣","🐥","🦆","🦅","🦉","🦇","🐺","🐗","🐴","🦄","🐝","🪱","🐛","🦋","🐌","🐞","🐜","🪲","🪳","🦟","🦗","🕷️","🦂","🐢","🐍","🦎","🦖","🦕","🐙","🦑","🦐","🦞","🦀","🐡","🐠","🐟","🐬","🐳","🐋","🦈","🐊","🐅","🐆","🦓","🦍","🦧","🐘"],
      "🍔": ["🍇","🍈","🍉","🍊","🍋","🍌","🍍","🥭","🍎","🍏","🍐","🍑","🍒","🍓","🫐","🥝","🍅","🫒","🥥","🥑","🍆","🥔","🥕","🌽","🌶️","🫑","🥒","🥬","🥦","🧄","🧅","🍄","🥜","🫘","🌰","🍞","🥐","🥖","🫓","🥨","🥯","🥞","🧇","🧀","🍖","🍗","🥩","🥓","🍔","🍟","🍕","🌭","🥪","🌮","🌯","🫔","🥙","🧆","🥚","🍳","🥘","🍲","🫕","🥣","🥗","🍿","🧈","🧂","🥫","🍱","🍘","🍙","🍚","🍛","🍜","🍝","🍠","🍢","🍣","🍤","🍥","🥮","🍡","🥟","🥠","🥡","🦀","🦞","🦐","🦑","🦪","🍦","🍧","🍨","🍩","🍪","🎂","🍰","🧁","🥧","🍫","🍬","🍭","🍮","🍯"],
      "⚽": ["⚽","🏀","🏈","⚾","🥎","🎾","🏐","🏉","🥏","🎱","🪀","🏓","🏸","🏒","🏑","🥍","🏏","🪃","🥅","⛳","🪁","🏹","🎣","🤿","🥊","🥋","🎽","🛹","🛼","🛷","⛸️","🥌","🎿","⛷️","🏂","🪂","🏋️","🤸","🤺","⛹️","🤾","🏌️","🏇","🧘","🏄","🏊","🤽","🚣","🧗","🚵","🚴","🏆","🥇","🥈","🥉","🏅","🎖️","🏵️","🎗️","🎪","🎭","🎨","🎬","🎤","🎧","🎼","🎹","🥁","🪘","🎷","🎺","🪗","🎸","🪕","🎻"],
      "🚗": ["🚗","🚕","🚙","🚌","🚎","🏎️","🚓","🚑","🚒","🚐","🛻","🚚","🚛","🚜","🏍️","🛵","🛺","🚲","🛴","🛹","🛼","🚏","🛣️","🛤️","⛽","🚨","🚥","🚦","🛑","🚧","⚓","⛵","🚤","🛳️","⛴️","🛥️","🚢","✈️","🛩️","🛫","🛬","🪂","💺","🚁","🚟","🚠","🚡","🛰️","🚀","🛸"],
      "💡": ["⌚","📱","📲","💻","⌨️","🖥️","🖨️","🖱️","🖲️","💾","💿","📀","📼","📷","📸","📹","🎥","📽️","🎞️","📞","☎️","📟","📠","📺","📻","🎙️","🎚️","🎛️","⏱️","⏲️","⏰","🕰️","💡","🔦","🕯️","🧯","💰","💴","💵","💶","💷","💳","💎","⚖️","🔧","🔨","⚒️","🛠️","⛏️","🪚","🔩","⚙️","🔗","⛓️","🪝","🧲","🪜","📈","📉","📊"]
    }

    const picker = document.createElement("div")
    picker.className = "live-chat__emoji-picker"

    const tabs = document.createElement("div")
    tabs.className = "live-chat__emoji-tabs"
    const grid = document.createElement("div")
    grid.className = "live-chat__emoji-grid"

    const categoryKeys = Object.keys(categories)
    categoryKeys.forEach((key, i) => {
      const tab = document.createElement("button")
      tab.className = `live-chat__emoji-tab${i === 0 ? " live-chat__emoji-tab--active" : ""}`
      tab.textContent = key
      tab.addEventListener("click", () => {
        tabs.querySelectorAll(".live-chat__emoji-tab").forEach(t => t.classList.remove("live-chat__emoji-tab--active"))
        tab.classList.add("live-chat__emoji-tab--active")
        this._renderEmojis(grid, categories[key])
      })
      tabs.appendChild(tab)
    })

    this._renderEmojis(grid, categories[categoryKeys[0]])

    picker.appendChild(tabs)
    picker.appendChild(grid)
    this.emojiBtnTarget.closest(".live-chat__input-area").appendChild(picker)
    this._emojiPicker = picker
  }

  _renderEmojis(grid, emojis) {
    grid.innerHTML = ""
    emojis.forEach(e => {
      const btn = document.createElement("button")
      btn.className = "live-chat__emoji-item"
      btn.textContent = e
      btn.addEventListener("click", () => {
        if (this.hasInputTarget) {
          this.inputTarget.value += e
          this.inputTarget.focus()
        }
        this._emojiPicker.remove()
        this._emojiPicker = null
      })
      grid.appendChild(btn)
    })
  }

  sendMessage() {
    if (!this.hasInputTarget) return
    const text = this.inputTarget.value.trim()
    if (!text || !this.channel) return

    this.channel.perform("send_message", { body: text })
    this.inputTarget.value = ""
  }

  // Private

  _subscribeToChannel() {
    const consumer = createConsumer()
    this.channel = consumer.subscriptions.create("LiveChatChannel", {
      received: (data) => {
        if (data.type === "history") {
          data.messages.forEach(msg => this._appendServerMessage(msg))
        } else if (data.type === "message") {
          this._appendServerMessage(data.message)
        } else if (data.type === "online_count") {
          this._updateOnline(data.count)
        }
      }
    })
  }

  _appendServerMessage(msg) {
    const el = document.createElement("div")
    el.className = "live-chat__msg"

    let avatarHtml
    if (msg.avatar_url) {
      avatarHtml = `<img src="${this._escape(msg.avatar_url)}" class="live-chat__msg-avatar" style="width:20px;height:20px;border-radius:50%;object-fit:cover;" alt="${this._escape(msg.username)}">`
    } else {
      avatarHtml = `<span class="live-chat__msg-avatar" style="background:var(--gradient-violet-electric)">${this._escape(msg.initials)}</span>`
    }

    const wrappedAvatar = this._wrapAvatarWithFrame(avatarHtml, msg)

    const badge = this._verifiedBadge(msg.verification_tier)
    const nameText = `${this._escape(msg.username)}`
    const nameHtml = msg.user_slug
      ? `<a href="/u/${this._escape(msg.user_slug)}" class="live-chat__msg-name">${nameText}</a>`
      : `<span class="live-chat__msg-name">${nameText}</span>`

    const isGif = /^https:\/\/media\d*\.giphy\.com\/media\/[^\s]+\.gif$/i.test(msg.body)
    const bodyHtml = isGif
      ? `<div class="live-chat__msg-gif"><img src="${this._escape(msg.body)}" class="comment__gif" loading="lazy" alt="GIF"></div>`
      : ` <span class="live-chat__msg-text">${this._escape(msg.body)}</span>`
    el.innerHTML = `${wrappedAvatar}<div class="live-chat__msg-content">${nameHtml}${badge}${bodyHtml}</div>`

    this.messagesTarget.appendChild(el)
    this.messageCount++

    if (this.messageCount > this.maxMessages) {
      this.messagesTarget.removeChild(this.messagesTarget.firstElementChild)
    }

    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  // Wrap the 20px avatar in the same concentric `.avatar-frame-wrapper`
  // the ERB surfaces use (#405), with the equipped frame overlay rendered
  // SERVER-SIDE (`msg.equipped_frame_html`, escaped by Rails/ViewComponent)
  // — see ChatMessageSerializable#equipped_frame_html_for. Injecting that
  // trusted fragment auto-connects the `dotlottie-player` Stimulus
  // controller (IntersectionObserver-gated, poster fix #223fe18 included),
  // so only on-screen messages animate. No frame → the bare avatar.
  //
  // The frame markup is the only HTML here we do NOT re-escape: it is
  // already a trusted server render. Everything user/admin-controlled
  // (asset url, tier, metadata) was escaped on the server. The wrapper's
  // own `data-tier` is validated against the tier allow-list as
  // defense-in-depth (CSS hook only).
  _wrapAvatarWithFrame(avatarHtml, msg) {
    const frameHtml = msg.equipped_frame_html || ""
    // Own messages always get a wrapper carrying the live-equip sync hook
    // (#419) — even with no frame yet — so when the viewer equips a frame,
    // self_frame_relay updates their own chat messages too. Other users'
    // frameless messages stay as the bare avatar (unchanged).
    const isSelf =
      this.currentUserId && String(msg.user_id) === String(this.currentUserId)
    if (!frameHtml && !isSelf) return avatarHtml

    const rawTier = msg.equipped_frame && msg.equipped_frame.tier
    const tier = this.frameTiers.includes(rawTier) ? rawTier : ""
    const tierAttr = tier ? ` data-tier="${tier}"` : ""
    const selfAttr = isSelf ? " data-self-avatar-frame" : ""

    return `<span class="avatar-frame-wrapper" style="position:relative;display:inline-block;width:20px;height:20px;"${tierAttr}${selfAttr}>${avatarHtml}${frameHtml}</span>`
  }

  _updateOnline(count) {
    if (!this.hasOnlineNumberTarget) return
    this.onlineNumberTarget.textContent = count
  }

  _verifiedBadge(tier) {
    if (!tier || tier === "none") return ""
    const color = tier === "gold" ? "#f4d107" : "#64ACF4"
    return `<svg class="verified-name__badge" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" style="vertical-align:middle;margin-left:2px;flex-shrink:0;"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.2786 3.762C10.1867 3.61509 10.0512 3.5005 9.8911 3.43418C9.73099 3.36787 9.55418 3.35312 9.38529 3.392L8.18662 3.66733C8.06378 3.69556 7.93613 3.69556 7.81329 3.66733L6.61462 3.392C6.44573 3.35312 6.26892 3.36787 6.1088 3.43418C5.94869 3.5005 5.81323 3.61509 5.72129 3.762L5.06795 4.80466C5.00129 4.91133 4.91129 5.00133 4.80462 5.06866L3.76195 5.722C3.6153 5.81386 3.50088 5.94909 3.43458 6.10894C3.36827 6.26878 3.35338 6.4453 3.39195 6.614L3.66729 7.814C3.69542 7.93663 3.69542 8.06403 3.66729 8.18666L3.39195 9.386C3.35323 9.55479 3.36805 9.73145 3.43436 9.89143C3.50067 10.0514 3.61517 10.1868 3.76195 10.2787L4.80462 10.932C4.91129 10.9987 5.00129 11.0887 5.06862 11.1953L5.72195 12.238C5.90995 12.5387 6.26862 12.6873 6.61462 12.608L7.81329 12.3327C7.93613 12.3044 8.06378 12.3044 8.18662 12.3327L9.38595 12.608C9.55475 12.6467 9.73141 12.6319 9.89139 12.5656C10.0514 12.4993 10.1867 12.3848 10.2786 12.238L10.932 11.1953C10.9986 11.0887 11.0886 10.9987 11.1953 10.932L12.2386 10.2787C12.3854 10.1866 12.4999 10.0511 12.5661 9.89102C12.6322 9.73091 12.6469 9.55415 12.608 9.38533L12.3333 8.18666C12.3051 8.06382 12.3051 7.93617 12.3333 7.81333L12.6086 6.614C12.6474 6.44527 12.6327 6.26866 12.5665 6.10868C12.5003 5.94871 12.3859 5.81332 12.2393 5.72133L11.196 5.068C11.0894 5.00121 10.9994 4.91118 10.9326 4.80466L10.2786 3.762ZM9.94329 6.51333C9.98452 6.43751 9.99474 6.34865 9.97179 6.26544C9.94884 6.18224 9.89451 6.11119 9.82023 6.06723C9.74596 6.02326 9.65753 6.00983 9.57355 6.02974C9.48957 6.04966 9.41658 6.10137 9.36995 6.174L7.62662 9.12466L6.57395 8.11666C6.54272 8.0846 6.50536 8.05915 6.46408 8.04184C6.4228 8.02453 6.37846 8.0157 6.3337 8.0159C6.28894 8.0161 6.24468 8.0253 6.20356 8.04297C6.16243 8.06065 6.12529 8.08642 6.09434 8.11875C6.06339 8.15109 6.03928 8.18933 6.02342 8.23119C6.00757 8.27305 6.00032 8.31767 6.00208 8.3624C6.00385 8.40712 6.01461 8.45103 6.03372 8.49151C6.05282 8.53199 6.07988 8.5682 6.11329 8.598L7.46929 9.89733C7.50558 9.93203 7.54924 9.95809 7.59702 9.97355C7.64479 9.98901 7.69544 9.99348 7.74518 9.98662C7.79493 9.97976 7.84248 9.96175 7.88428 9.93394C7.92609 9.90612 7.96107 9.86923 7.98662 9.826L9.94329 6.51333Z" fill="${color}"/></svg>`
  }

  _escape(str) {
    const d = document.createElement("div")
    d.textContent = str
    return d.innerHTML
  }
}
